///
/// EarthBrowser
///
/// Created by Matt Giger on 8/12/2024
/// Copyright (c) 2024 Geodata Labs
///

import { CacheTable } from "@/earthbrowser/database/tables/CacheTable"
import { Geotiff } from "@/earthbrowser/formats/Geotiff"
import { type Texture } from "@stovokor/shader/Texture"
import { FetchLoader, type FetchLoaderOptions } from "@/earthbrowser/utils/loaders/FetchLoader"
import { logger } from "@/utils/LogDispatcher"

/**
 * ImageLoader class extends FetchLoader to handle image loading and caching.
 */
export class ImageLoader extends FetchLoader {
	/**
	 * Constructs a new ImageLoader instance.
	 * @param url The URL to fetch from.
	 * @param options Optional configuration (lifetime, authToken, getAuthToken)
	 */
	constructor(url: string, options?: FetchLoaderOptions | number) {
		super(url, options)
	}

	/** Indicates if the image is valid */
	public valid: boolean = false
	/** The server answered with an empty body (e.g. a 204 for a sparse
	 *  tile with no data): a definitive "nothing here", not a failure */
	public noContent: boolean = false
	/** Width of the loaded image */
	private width: number = 0
	/** Height of the loaded image */
	private height: number = 0
	/** Metadata of the loaded image */
	public metadata: Record<string, string | number> = {}
	/** Temporary reference to the image being loaded */
	private _image?: HTMLImageElement

	/**
	 * Loads the image into a texture.
	 * @param texture - The texture to load the image into
	 * @returns A promise that resolves to true if the image was loaded successfully, false otherwise
	 */
	public async loadIntoTexture(texture: Texture): Promise<boolean> {
		texture.available = false

		const success = await this.loadCachedImageIntoTexture(texture, false)
		if(success && !this._aborted) {
			texture.available = true
			return true
		}

		return false
	}

	/**
	 * Loads the image into a texture as R8 grayscale (1/4 memory of RGBA).
	 * Use for single-channel data like radar or elevation.
	 * @param texture - The texture to load the image into
	 * @param mipmaps - Generate mipmaps (for high-frequency fields that get minified on screen)
	 * @returns A promise that resolves to true if the image was loaded successfully, false otherwise
	 */
	public async loadGrayscaleIntoTexture(texture: Texture, mipmaps: boolean = false): Promise<boolean> {
		texture.available = false

		const success = await this.loadCachedImageIntoTexture(texture, true, mipmaps)
		if(success && !this._aborted) {
			texture.available = true
			return true
		}

		return false
	}

	/**
	 * Fetch (through the image cache) and decode the image without touching
	 * a texture, for callers that assemble several images into one texture.
	 * Resolves undefined on failure, abort, or an empty (204) body.
	 */
	public async loadImage(): Promise<HTMLImageElement | undefined> {
		if(this._aborted) {
			return undefined
		}
		let buffer = await CacheTable.get(this.url)
		if(!buffer) {
			buffer = await this.load()
			if(!buffer) {
				this.valid = false
				return undefined
			}
			CacheTable.set(this.url, Date.now() + this._lifetime, buffer)
		}
		if(buffer.byteLength === 0) {
			this.noContent = true
			this.valid = false
			return undefined
		}
		const bloburl = URL.createObjectURL(new Blob([buffer], { type: this.detectMimeType(buffer) }))
		try {
			const image = new Image()
			this._image = image
			image.src = bloburl
			await image.decode()
			if(this._aborted) {
				return undefined
			}
			this.width = image.width
			this.height = image.height
			this.valid = true
			return image
		} catch(error) {
			logger.log('Error decoding image:', error instanceof Error ? error.message : error, this.url)
			this.valid = false
			return undefined
		} finally {
			this._image = undefined
			URL.revokeObjectURL(bloburl)
		}
	}

	/**
	 * Aborts the image loading process.
	 */
	override abort(): void {
		super.abort()
		if(this._image) {
			this._image.src = ''
			this._image = undefined
		}
		this.valid = false
	}

	/**
	 * Loads a cached image or fetches and caches a new one.
	 * @returns A promise that resolves to the loaded image or undefined if loading fails
	 */
	private async loadCachedImageIntoTexture(texture: Texture, grayscale: boolean, mipmaps: boolean = false): Promise<boolean> {
		if(this._aborted) {
			return false
		}

		// try to load a cached image first
		let buffer = await CacheTable.get(this.url)
		if(!buffer) {
			buffer = await this.load()
			if(!buffer) {
				this.valid = false
				return false
			}

			// save the image to the cache
			CacheTable.set(this.url, Date.now() + this._lifetime, buffer)
		}

		if(buffer.byteLength === 0) {
			this.noContent = true
			this.valid = false
			return false
		}

		if(this.url.endsWith('.tiff') || this.url.endsWith('.tif')) {
			return this.extractTIFFImage(buffer, texture)
		} else {
			// Determine MIME type from URL or buffer magic bytes
			const mimeType = this.detectMimeType(buffer)
			return this.extractImage(new Blob([buffer], { type: mimeType }), texture, grayscale, mipmaps)
		}
	}

	/**
	 * Detect MIME type from URL extension or buffer magic bytes
	 */
	private detectMimeType(buffer: ArrayBuffer): string {
		// Check magic bytes first
		const bytes = new Uint8Array(buffer, 0, Math.min(12, buffer.byteLength))

		// WebP: starts with "RIFF" + 4 bytes + "WEBP"
		if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
			bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) {
			return 'image/webp'
		}

		// PNG: starts with 0x89 PNG
		if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
			return 'image/png'
		}

		// JPEG: starts with 0xFF 0xD8 0xFF
		if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
			return 'image/jpeg'
		}

		// Fallback to URL extension
		const url = this.url.toLowerCase()
		if (url.endsWith('.webp')) return 'image/webp'
		if (url.endsWith('.png')) return 'image/png'
		if (url.endsWith('.jpg') || url.endsWith('.jpeg')) return 'image/jpeg'
		if (url.endsWith('.gif')) return 'image/gif'
		if (url.endsWith('.avif')) return 'image/avif'

		// Default
		return 'image/png'
	}

	private async extractTIFFImage(buffer: ArrayBuffer, texture: Texture): Promise<boolean> {
		if(this._aborted) {
			this.valid = false
			return false
		}

		const tiff = new Geotiff(buffer)
		const image = tiff.entries[0]
		if(image?.valid) {
			this.valid = true
			this.width = image.width
			this.height = image.height
			const imageData = image.readImageData()
			this.metadata = image.metadata
			texture.uploadArrayBuffer(imageData, this.width, this.height, image.samplesPerPixel, image.bitsPerSample)
			return true
		}

		this.valid = false
		return false
	}

	private async extractImage(blob: Blob, texture: Texture, grayscale: boolean = false, mipmaps: boolean = false): Promise<boolean> {

		const bloburl = URL.createObjectURL(blob)
		try {
			const image = new Image()
			this._image = image
			image.src = bloburl
			await image.decode()

			// The load may have been aborted (tile destroyed) while decoding -
			// don't upload into a texture we no longer own
			if(this._aborted) {
				this.valid = false
				return false
			}

			this.width = image.width
			this.height = image.height

			if (grayscale) {
				// Extract R channel only for memory efficiency
				texture.uploadGrayscale(image, this.width, this.height, mipmaps)
			} else {
				texture.uploadCanvas(image, this.width, this.height)
			}
			this.valid = true

			// Log radar PNG URL for inspection
			if (this.url.includes('nexrad') || this.url.includes('radar')) {
				console.log(`🔗 RADAR PNG ATLAS: ${this.url} (${this.width}x${this.height})`)
			}
		} catch(error) {
			if(error instanceof Error) {
				logger.log('Error loading cached image:', error.message, this.url)
			} else {
				logger.log('Error loading cached image:', error, this.url)
			}
			this.valid = false
		} finally {
			this._image = undefined
			URL.revokeObjectURL(bloburl)
		}

		return this.valid
	}
}
